import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { Formik } from "formik";
import axios from "axios";
import {
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Alert as MuiAlert,
  Paper as MuiPaper,
  Card as MuiCard,
  CardContent,
  Button as MuiButton,
  Divider,
  Typography,
  IconButton,
  Box,
  Checkbox,
} from "@mui/material";
import { spacing } from "@mui/system";
import SelectWrapper from "./SelectBoxNoFormik";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import moment from "moment";
import ViewCustomerInvoiceDialog from "../components/ViewCustomerInvoiceDialog";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const Card = styled(MuiCard)(spacing);
const Paper = styled(MuiPaper)(spacing);
const Alert = styled(MuiAlert)(spacing);
const Button = styled(MuiButton)(spacing);

const initialValues = {
  name: "",
  jobTitle: "",
  email: "",
  mobile: "",
  directNumber: "",
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Required"),
  jobTitle: Yup.string().required("Required"),
  email: Yup.string().required("Required"),
  mobile: Yup.string().required("Required"),
  directNumber: Yup.string().required("Required"),
});

const toCountryKey = (countryId) => String(countryId);

const AddMultipleClaimForm = (props) => {
  const { customerId, customerNumber, refreshTable, canWriteClaims = false } = props;
  const [supplierData, setSupplierData] = useState([]);

  useEffect(() => {
    async function fetchData() {
      axios.get("/api/v1/suppliers").then((response) => setSupplierData(response.data));
    }
    fetchData();
  }, []);

  const [currencyData, setCurrencyData] = useState([]);

  useEffect(() => {
    axios.get("/api/v1/currencies").then((response) => setCurrencyData(response.data));
  }, []);

  const [productData, setProductData] = useState([]);

  useEffect(() => {
    async function fetchData() {
      axios.get("/api/v1/productTypes").then((response) => setProductData(response.data));
    }
    fetchData();
  }, []);

  const [invoiceStateData, setInvoiceStateData] = useState([]);

  useEffect(() => {
    async function fetchData() {
      axios.get("/api/v1/invoiceStates").then((response) => setInvoiceStateData(response.data));
    }
    fetchData();
  }, []);

  const [invoiceLabelData, setInvoiceLabelData] = useState([]);

  useEffect(() => {
    async function fetchData() {
      axios.get("/api/v1/invoiceLabels").then((response) => setInvoiceLabelData(response.data));
    }
    fetchData();
  }, []);

  function generateArrayOfYears() {
    var max = new Date().getFullYear() + 1;
    var min = 2021;
    var years = [];

    for (var i = max; i >= min; i--) {
      years.push({ yearId: i, yearText: i });
    }
    return years;
  }

  let yearArr = generateArrayOfYears().reverse();

  const quarterArr = [
    { quarterId: 1, quarterText: "Q1", startMonth: 1, endMonth: 3 },
    { quarterId: 2, quarterText: "Q2", startMonth: 4, endMonth: 6 },
    { quarterId: 3, quarterText: "Q3", startMonth: 7, endMonth: 9 },
    { quarterId: 4, quarterText: "Q4", startMonth: 10, endMonth: 12 },
    { quarterId: 5, quarterText: "Year", startMonth: 1, endMonth: 12 },
  ];

  function FormDialog() {
    // Consts
    const [invoiceGridData, setInvoiceGridData] = useState([]);
    const [selectedYear, setSelectedYear] = useState("");
    const [selectedStartMonth, setSelectedStartMonth] = useState("");
    const [selectedEndMonth, setSelectedEndMonth] = useState("");
    const [selectedQuarter, setSelectedQuarter] = useState("");
    const [selectedClaimPreview, setSelectedClaimPreview] = useState([]);

    const [open, setOpen] = useState(false);
    const [claimAdded, setClaimAdded] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [excludedCountries, setExcludedCountries] = useState(new Set());

    var invoiceCount = selectedClaimPreview.invoiceCount ? selectedClaimPreview.invoiceCount : 0;
    var totalGrossAmount = selectedClaimPreview.totalGrossAmount ? selectedClaimPreview.totalGrossAmount : 0;
    var totalVATAmount = selectedClaimPreview.totalVATAmount ? selectedClaimPreview.totalVATAmount : 0;
    var totalVATAmountEur = selectedClaimPreview.totalVATAmountEUR ? selectedClaimPreview.totalVATAmountEUR : 0;
    var missingInvoiceCopies = selectedClaimPreview.missingInvoiceCopies ? selectedClaimPreview.missingInvoiceCopies : 0;

    const columns = [
      {
        field: "exclude",
        headerName: "Exclude",
        flex: 0.2,
        renderCell: (params) => (
          <Checkbox
            checked={excludedCountries.has(toCountryKey(params.row.countryId))}
            onChange={(event) => {
              const countryId = toCountryKey(params.row.countryId);
              const newExcludedCountries = new Set(excludedCountries);
              if (event.target.checked) {
                newExcludedCountries.add(countryId);
              } else {
                newExcludedCountries.delete(countryId);
              }
              setExcludedCountries(newExcludedCountries);
            }}
            color="primary"
          />
        ),
      },
      {
        field: "countryCode",
        headerName: "Country",
        flex: 0.3,
        valueGetter: (params) => countryData.find((country) => country.countryId === params.row.countryId)?.countryCode || "Unknown",
      },
      {
        field: "claimCode",
        headerName: "Claim Code",
        flex: 0.6,
        valueGetter: (params) => {
          const countryKey = toCountryKey(params.row.countryId);
          const paddedCountryId = countryKey.padStart(2, "0");
          const countryInvoices = selectedClaimPreview?.invoicesByCountry?.[countryKey] || [];
          const preFinanceState = countryInvoices.some((invoice) => invoice.preFinanceId && invoice.preFinanceId > 0) ? "1" : "0";
          const paymentToDelta = preFinanceState === "1" ? "1" : "0";
          return `${customerNumber}${paddedCountryId}${selectedYear.toString().slice(2)}${params.row.startMonth
            .toString()
            .padStart(2, "0")}${params.row.endMonth.toString().padStart(2, "0")}0${preFinanceState}${paymentToDelta}`;
        },
      },
      {
        field: "invoiceCount",
        headerName: "Invoice Count",
        flex: 0.3,
        align: "center",
      },
      {
        field: "totalGrossAmount",
        headerName: "Total Net Amount",
        flex: 0.4,
        align: "right",
        renderCell: (params) => (
          <div style={{ textAlign: "right", paddingRight: "15px" }}>{params.value.toFixed(2).replace(".", ",")}</div>
        ),
      },
      {
        field: "totalVATAmount",
        headerName: "Total VAT Amount",
        flex: 0.4,
        align: "right",
        renderCell: (params) => (
          <div style={{ textAlign: "right", paddingRight: "15px" }}>{params.value.toFixed(2).replace(".", ",")}</div>
        ),
      },
      {
        field: "totalVATAmountEUR",
        headerName: "Total VAT EUR",
        flex: 0.4,
        align: "right",
        renderCell: (params) => (
          <div style={{ textAlign: "right", paddingRight: "15px" }}>{params.value.toFixed(2).replace(".", ",")}</div>
        ),
      },
      {
        field: "missingInvoiceCopies",
        headerName: "Missing Copies",
        flex: 0.3,
        align: "center",
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.3,
        sortable: false,
        renderCell: (params) => {
          return (
            <Box>
              <IconButton onClick={() => handleViewInvoices(params.row)}>
                <VisibilityIcon />
              </IconButton>
            </Box>
          );
        },
      },
    ];

    const [viewInvoicesDialog, setViewInvoicesDialog] = useState({
      isOpen: false,
      title: "",
      invoices: [],
    });

    const [viewCustomerInvoiceDialog, setViewCustomerInvoiceDialog] = useState({
      isOpen: false,
      title: "",
      subTitle: "",
      custInfo: [],
    });

    const [countryData, setCountryData] = useState([]);

    useEffect(() => {
      async function fetchData() {
        axios.get("/api/v1/countries").then((response) => setCountryData(response.data));
      }
      fetchData();
    }, []);

    // Helper function to check if amount meets minimum requirement
    const checkMinimumAmount = (countryId, totalVATAmount, selectedQuarter) => {
      const country = countryData.find((c) => c.countryId === countryId);
      if (!country) return true; // If country not found, assume valid

      const isYearly = selectedQuarter === 5; // Quarter 5 is "Year"
      const minimumAmount = isYearly ? country.minYear : country.minQuarter;

      return totalVATAmount >= minimumAmount;
    };

    // Helper function to get row class name based on minimum amount validation
    const getRowClassName = (params) => {
      const isValid = checkMinimumAmount(params.row.countryId, params.row.totalVATAmount, selectedQuarter);
      return isValid ? "pastelGreen" : "pastelRed";
    };

    // Color Legend Component
    const ColorLegend = () => (
      <Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} mb={2}>
        <Box display="flex" alignItems="center" mr={2}>
          <Box width={15} height={15} bgcolor="#b2f2bb" mr={0.5} />
          <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
            Minimum amount requirement met
          </Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <Box width={15} height={15} bgcolor="#ffc9c9" mr={0.5} />
          <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
            Minimum amount requirement not met
          </Typography>
        </Box>
      </Box>
    );

    const handleSubmit = () => {};

    const handleYearValue = (value, name) => {
      setSelectedYear(value);
    };

    const handleQuarterValue = (value, name) => {
      setSelectedQuarter(value);
      // Auto-set start and end months based on quarter
      const quarter = quarterArr.find((q) => q.quarterId === parseInt(value));
      if (quarter) {
        setSelectedStartMonth(quarter.startMonth);
        setSelectedEndMonth(quarter.endMonth);
        console.log(`Quarter ${quarter.quarterText} selected: Start Month ${quarter.startMonth}, End Month ${quarter.endMonth}`);
      }
    };

    const handlePreview = async () => {
      getPreviewData();
    };

    const handleViewInvoices = (claimRow) => {
      const countryKey = toCountryKey(claimRow.countryId);
      const countryInvoices = selectedClaimPreview.invoicesByCountry[countryKey] || [];
      const countryName = countryData.find((c) => c.countryId === claimRow.countryId)?.countryCode || "Country";

      console.log("Opening invoice dialog for country:", countryKey, "with invoices:", countryInvoices);

      // Open our custom invoice dialog
      setViewInvoicesDialog({
        isOpen: true,
        title: `Invoices for ${countryName} Claim`,
        invoices: countryInvoices,
      });
    };

    // Handle Edit Confirmation for invoice updates
    function onEditConfirmed(e, params) {
      console.log("Invoice updated successfully, refreshing data...");

      // Close the invoice dialog first
      setViewCustomerInvoiceDialog({
        ...viewCustomerInvoiceDialog,
        isOpen: false,
      });

      // Refresh the preview data to get updated invoice information
      getPreviewData();

      // If the invoice list dialog is open, refresh it directly with fresh API data
      if (viewInvoicesDialog.isOpen) {
        // Find which country dialog is currently open by looking at the current invoices
        const currentInvoices = viewInvoicesDialog.invoices;
        if (currentInvoices.length > 0) {
          const countryId = currentInvoices[0].countryCode;
          console.log("Refreshing invoice list for country:", countryId);

          // Fetch fresh invoice data for this specific country
          const refreshInvoiceList = async () => {
            try {
              const response = await axios.get(`/api/v1/customerInvoicesByEntityIdWithProductCodes/${customerId}`);
              const countryInvoices = response.data.filter((invoice) => invoice.countryCode === countryId);

              setViewInvoicesDialog({
                ...viewInvoicesDialog,
                invoices: countryInvoices,
              });
              console.log("Updated invoice list for country:", countryId, "with", countryInvoices.length, "invoices");
            } catch (error) {
              console.error("Error refreshing invoice list:", error);
            }
          };

          refreshInvoiceList();
        }
      }
    }

    // Handle File function (required by ViewCustomerInvoiceDialog)
    const handleFile = (updatedData) => {
      console.log("File handled:", updatedData);
      // This function is called when file operations are performed
      // In our case, we just refresh the data
      getPreviewData();
    };

    const getPreviewData = async () => {
      // Ensure we have the correct months based on selected quarter
      let startMonth = selectedStartMonth;
      let endMonth = selectedEndMonth;

      if (selectedQuarter) {
        const quarter = quarterArr.find((q) => q.quarterId === parseInt(selectedQuarter));
        if (quarter) {
          startMonth = quarter.startMonth;
          endMonth = quarter.endMonth;
        }
      }

      const test = {
        entityId: parseInt(customerId, 0),
        country: null, // No country selection for quarterly mode
        year: selectedYear,
        startMonth: startMonth,
        endMonth: endMonth,
      };

      console.log("Preview data:", test);

      const response = await axios.post("/api/v1/claims/claimPreviewNoCountry", test);
      // Group invoices by country (exclude state 4 invoices)
      const invoicesByCountry = {};
      response.data.customerInvoices
        .filter((invoice) => invoice.invoiceStateId !== 4) // Exclude removed invoices
        .forEach((invoice) => {
          const countryId = toCountryKey(invoice.countryCode);
          if (!invoicesByCountry[countryId]) {
            invoicesByCountry[countryId] = [];
          }
          invoicesByCountry[countryId].push(invoice);
        });

      console.log("Invoices grouped by country:", invoicesByCountry);

      // Calculate totals per country
      const countryTotals = {};
      Object.keys(invoicesByCountry).forEach((countryId) => {
        const countryInvoices = invoicesByCountry[countryId];
        countryTotals[countryId] = {
          invoiceCount: countryInvoices.length,
          totalGrossAmount: countryInvoices.reduce((sum, inv) => sum + inv.invoiceGrossAmount, 0),
          totalVATAmount: countryInvoices.reduce((sum, inv) => sum + inv.invoiceVATAmountLocalCurrency, 0),
          totalVATAmountEUR: countryInvoices.reduce((sum, inv) => sum + inv.invoiceVATAmountEUR, 0),
          missingInvoiceCopies: countryInvoices.filter((inv) => !inv.invoiceCopy).length,
        };
      });

      console.log("Country totals:", countryTotals);

      // Create claim rows for each country
      const claimRows = Object.keys(countryTotals).map((countryId) => {
        const countryTotal = countryTotals[countryId];
        return {
          id: `claim-${countryId}`, // Unique ID for DataGrid
          countryId: parseInt(countryId), // Convert back to integer for display
          startMonth: startMonth,
          endMonth: endMonth,
          invoiceCount: countryTotal.invoiceCount,
          totalGrossAmount: countryTotal.totalGrossAmount,
          totalVATAmount: countryTotal.totalVATAmount,
          totalVATAmountEUR: countryTotal.totalVATAmountEUR,
          missingInvoiceCopies: countryTotal.missingInvoiceCopies,
        };
      });

      // Calculate totals from filtered invoices (exclude state 4)
      const filteredInvoices = response.data.customerInvoices.filter((invoice) => invoice.invoiceStateId !== 4);
      const filteredTotals = {
        invoiceCount: filteredInvoices.length,
        totalGrossAmount: filteredInvoices.reduce((sum, inv) => sum + inv.invoiceGrossAmount, 0),
        totalVATAmount: filteredInvoices.reduce((sum, inv) => sum + inv.invoiceVATAmountLocalCurrency, 0),
        totalVATAmountEUR: filteredInvoices.reduce((sum, inv) => sum + inv.invoiceVATAmountEUR, 0),
        missingInvoiceCopies: filteredInvoices.filter((inv) => !inv.invoiceCopy).length,
      };

      // Store grouped data
      setSelectedClaimPreview({
        invoicesByCountry: invoicesByCountry,
        countryTotals: countryTotals,
        invoiceCount: filteredTotals.invoiceCount,
        totalGrossAmount: filteredTotals.totalGrossAmount,
        totalVATAmount: filteredTotals.totalVATAmount,
        totalVATAmountEUR: filteredTotals.totalVATAmountEUR,
        missingInvoiceCopies: filteredTotals.missingInvoiceCopies,
      });

      // Reset excluded countries when preview is refreshed
      setExcludedCountries(new Set());
      setInvoiceGridData(claimRows);
    };

    const handleAddClaim = async () => {
      if (!canWriteClaims) {
        return;
      }

      let hasErrors = false;
      if (!selectedYear) {
        hasErrors = true;
      }
      if (!selectedQuarter) {
        hasErrors = true;
      }
      if (!selectedClaimPreview.invoicesByCountry || Object.keys(selectedClaimPreview.invoicesByCountry).length === 0) {
        hasErrors = true;
      }
      if (hasErrors) {
        return false;
      }

      // Ensure we have the correct months based on selected quarter
      let startMonth = selectedStartMonth;
      let endMonth = selectedEndMonth;

      if (selectedQuarter) {
        const quarter = quarterArr.find((q) => q.quarterId === parseInt(selectedQuarter));
        if (quarter) {
          startMonth = quarter.startMonth;
          endMonth = quarter.endMonth;
        }
      }

      try {
        const invoicesByCountry = selectedClaimPreview.invoicesByCountry;
        const countryTotals = selectedClaimPreview.countryTotals;
        const createdClaims = [];
        const invoicesToUpdateState = []; // Array to collect invoices that need state update
        const duplicateClaimCodes = []; // Array to collect duplicate claim codes

        console.log("Creating claims for countries:", Object.keys(invoicesByCountry));

        // Create a claim for each country
        for (const countryId of Object.keys(invoicesByCountry)) {
          // Skip excluded countries
          if (excludedCountries.has(countryId)) {
            console.log(`Skipping excluded country ${countryId}`);
            continue;
          }

          const countryInvoices = invoicesByCountry[countryId];
          const countryTotal = countryTotals[countryId];

          console.log(`Processing country ${countryId}:`, {
            invoiceCount: countryInvoices.length,
            totalVATAmount: countryTotal.totalVATAmount,
            selectedQuarter: selectedQuarter,
          });

          // Check if minimum amount requirement is met
          const meetsMinimumAmount = checkMinimumAmount(parseInt(countryId), countryTotal.totalVATAmount, selectedQuarter);
          console.log(`Country ${countryId} meets minimum amount:`, meetsMinimumAmount);

          if (!meetsMinimumAmount) {
            console.log(`Country ${countryId} does not meet minimum amount requirement. Setting invoices to state 2.`);

            // Add invoices to state update array (only if current state is 3)
            countryInvoices.forEach((invoice) => {
              if (invoice.invoiceStateId === 3) {
                invoicesToUpdateState.push({
                  customerInvoiceId: invoice.customerInvoiceId,
                  invoiceStateId: 2,
                });
              }
            });

            // Skip creating claim for this country
            continue;
          }

          // Determine pre-finance state based on invoices for this country
          const preFinanceState = countryInvoices.some((invoice) => invoice.preFinanceId && invoice.preFinanceId > 0) ? "1" : "0";

          let claimArray = {
            entityId: parseInt(customerId, 0),
            claimCode:
              customerNumber +
              countryId.toString().padStart(2, "0") + // Use country ID with 2 digits
              selectedYear.toString().slice(2) +
              startMonth.toString().padStart(2, "0") +
              endMonth.toString().padStart(2, "0") +
              "0" + // Claim correction (always 0)
              preFinanceState + // Pre-financed (1 or 0)
              (preFinanceState === "1" ? "1" : "0"), // Payment to delta (1 or 0)
            startMonth: startMonth,
            endMonth: endMonth,
            year: selectedYear,
            countryId: parseInt(countryId), // Convert string to int for API
            claimGrossAmount: countryTotal.totalGrossAmount,
            claimVATAmountLocCur: countryTotal.totalVATAmount,
            claimVATAmountEUR: countryTotal.totalVATAmountEUR,
            paymentTypeId: 2,
            paymentToDelta: preFinanceState === "1" ? 1 : 0,
            claimStateId: 1,
          };

          console.log(`Creating claim for country ${countryId}:`, claimArray);

          try {
            // Create the claim
            const claimResponse = await axios.post("/api/v1/claims/claimInitialCreate", claimArray);
            const initClaimId = claimResponse.data.claimId;
            createdClaims.push({ claimId: initClaimId, countryId: countryId });

            // Update invoices for this country - link them to the claim
            const invoicesToBeUpdated = countryInvoices
              .filter((row) => row.invoiceStateId.toString() !== "4")
              .map((row) => {
                // Convert productCodeDescriptionLanguageCountry to integer or null
                let languageCountryId = null;
                if (row.productCodeDescriptionLanguageCountry) {
                  const parsed = parseInt(row.productCodeDescriptionLanguageCountry, 10);
                  languageCountryId = isNaN(parsed) ? null : parsed;
                }

                return {
                  ...row, // Send all fields from the invoice row
                  claimId: initClaimId, // Override with the new claim ID
                  // invoiceStateId: 3, // Set state to 1 for invoices included in claims
                  productCodeDescriptionLanguageCountry: languageCountryId,
                };
              });

            await axios.put("/api/v1/customerInvoices/updateInvoiceClaimGridBulk", invoicesToBeUpdated);

            console.log(`Successfully created claim ${initClaimId} for country ${countryId}`);
          } catch (error) {
            console.error(`Failed to create claim for country ${countryId}:`, error);

            // Check if it's a duplicate key error
            if (
              error.response?.data?.includes?.("duplicate key") ||
              error.message?.includes?.("duplicate key") ||
              error.response?.status === 400
            ) {
              console.log(`Claim code ${claimArray.claimCode} already exists for country ${countryId}. Skipping this country.`);
              duplicateClaimCodes.push({
                claimCode: claimArray.claimCode,
                countryId: countryId,
                countryName: countryData.find((c) => c.countryId === parseInt(countryId))?.countryName || `Country ${countryId}`,
              });
              // Continue to next country instead of stopping
              continue;
            } else {
              // For other errors, still continue but log them
              console.error(`Unexpected error for country ${countryId}:`, error);
              continue;
            }
          }
        }

        // Update invoice states for countries that don't meet minimum amount
        if (invoicesToUpdateState.length > 0) {
          console.log(`Updating ${invoicesToUpdateState.length} invoices to state 2 (minimum amount not met)`);
          const jsonRowsToBeUpdated = JSON.stringify(invoicesToUpdateState);
          await axios.put("/api/v1/customerInvoices/updateInvoiceStateId", jsonRowsToBeUpdated, {
            headers: {
              "Content-Type": "application/json",
            },
          });
        }

        console.log("Successfully created claims:", createdClaims);
        console.log("Duplicate claim codes found:", duplicateClaimCodes);

        // Show success message with details
        if (createdClaims.length > 0) {
          setClaimAdded(true);
          refreshTable();
          setTimeout(() => {
            setClaimAdded(false);
          }, 5000);
        }

        // Show warning if there were duplicate claim codes
        if (duplicateClaimCodes.length > 0) {
          const duplicateMessage = duplicateClaimCodes.map((dup) => `${dup.claimCode} (${dup.countryName})`).join(", ");
          setErrorMessage(`Some claims were skipped due to duplicate claim codes: ${duplicateMessage}`);
        } else if (createdClaims.length === 0) {
          setErrorMessage("No claims were created. All claim codes may already exist for the selected period.");
        }
      } catch (error) {
        console.error("Error creating claims:", error);

        // Check if it's a duplicate key error
        if (error.response?.data?.includes?.("duplicate key") || error.message?.includes?.("duplicate key")) {
          setErrorMessage("One or more claim codes already exist for the selected period. Please check your data and try again.");
        } else {
          setErrorMessage("Something went wrong while creating the claims. Please try again.");
        }
      }
    };

    return (
      <Card mb={0} sx={{ mt: 1, mb: -1, ml: -3, p: 0 }}>
        <CardContent>
          <Paper mt={0}>
            <div>
              <Button
                variant="contained"
                color="secondary"
                sx={
                  canWriteClaims
                    ? { backgroundColor: "#f57c00", "&:hover": { backgroundColor: "#ef6c00" } }
                    : undefined
                }
                onClick={() => setOpen(true)}
                disabled={!canWriteClaims}
              >
                Create Pre-Claims
              </Button>
              <Dialog open={open} aria-labelledby="form-dialog-title" fullWidth maxWidth="xl">
                <DialogTitle id="form-dialog-title">Add Pre-Claims (Multiple) for customer {customerNumber}</DialogTitle>
                <Divider />
                <DialogContent>
                  <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                    {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values, status }) => (
                      <Card mb={6}>
                        <CardContent>
                          {claimAdded ? (
                            <Alert severity="error" my={3}>
                              {errorMessage}
                            </Alert>
                          ) : errorMessage.length > 0 ? (
                            <Alert severity="success" my={3}>
                              Your data has been submitted successfully!
                            </Alert>
                          ) : (
                            ""
                          )}
                          <Grid container xs={12} sx={{ mb: 5 }}>
                            <Grid container xs={6} spacing={5} sx={{ pr: 2 }}>
                              <Grid item xs={5}>
                                <SelectWrapper
                                  name="year"
                                  label="Year"
                                  options={JSON.parse(
                                    JSON.stringify(JSON.parse(JSON.stringify(yearArr).split('"yearId":').join('"key":')))
                                      .split('"yearText":')
                                      .join('"value":')
                                  )}
                                  handleChange={handleYearValue}
                                />
                              </Grid>
                              <Grid item xs={5}>
                                <SelectWrapper
                                  name="quarter"
                                  label="Period"
                                  options={JSON.parse(
                                    JSON.stringify(JSON.parse(JSON.stringify(quarterArr).split('"quarterId":').join('"key":')))
                                      .split('"quarterText":')
                                      .join('"value":')
                                  )}
                                  handleChange={handleQuarterValue}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <Button onClick={handlePreview} sx={{ mt: 6 }} variant="outlined">
                                  Preview Pre-Claims
                                </Button>
                              </Grid>
                            </Grid>
                            <Grid item xs={6} backgroundColor="#376fd0">
                              <Grid item xs={12} sx={{ p: 5, pt: 3 }}>
                                <Grid container xs={12}>
                                  <Grid item xs={5}>
                                    <Typography sx={{ fontSize: 22, color: "white" }}>Invoice Count:</Typography>
                                  </Grid>
                                  <Grid item xs={7}>
                                    <Typography sx={{ fontSize: 22, color: "white" }}>{invoiceCount}</Typography>
                                  </Grid>
                                </Grid>
                                <Grid container xs={12}>
                                  <Grid item xs={5}>
                                    <Typography sx={{ fontSize: 22, color: "white" }}>Total Net Amount:</Typography>
                                  </Grid>
                                  <Grid item xs={7}>
                                    <Typography sx={{ fontSize: 22, color: "white" }}>{totalGrossAmount.toFixed(2)}</Typography>
                                  </Grid>
                                </Grid>
                                <Grid container xs={12}>
                                  <Grid item xs={5}>
                                    <Typography sx={{ fontSize: 22, color: "white" }}>Total VAT Amount:</Typography>
                                  </Grid>
                                  <Grid item xs={7}>
                                    <Typography sx={{ fontSize: 22, color: "white" }}>{totalVATAmount.toFixed(2)}</Typography>
                                  </Grid>
                                </Grid>
                                <Grid container xs={12}>
                                  <Grid item xs={5}>
                                    <Typography sx={{ fontSize: 22, color: "white" }}>Total VAT Amount EUR:</Typography>
                                  </Grid>
                                  <Grid item xs={7}>
                                    <Typography sx={{ fontSize: 22, color: "white" }}>{totalVATAmountEur.toFixed(2)}</Typography>
                                  </Grid>
                                </Grid>
                                <Grid container xs={12}>
                                  <Grid item xs={5}>
                                    <Typography sx={{ fontSize: 22, color: "white" }}>Missing Invoice Copies:</Typography>
                                  </Grid>
                                  <Grid item xs={7}>
                                    <Typography sx={{ fontSize: 22, color: "white" }}>{missingInvoiceCopies}</Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12}>
                            {/* Custom Invoice View Dialog */}
                            <Dialog
                              open={viewInvoicesDialog.isOpen}
                              onClose={() => setViewInvoicesDialog({ isOpen: false, title: "", invoices: [] })}
                              aria-labelledby="invoice-dialog-title"
                              aria-describedby="invoice-dialog-description"
                              fullWidth
                              maxWidth="xl"
                            >
                              <DialogTitle id="invoice-dialog-title">{viewInvoicesDialog.title}</DialogTitle>
                              <Divider />
                              <DialogContent>
                                <Card mb={6}>
                                  <CardContent>
                                    <DataGrid
                                      getRowId={(row) => row.customerInvoiceId}
                                      disableSelectionOnClick
                                      disableColumnSelector
                                      components={{ Toolbar: GridToolbar }}
                                      density="compact"
                                      componentsProps={{
                                        toolbar: {
                                          csvOptions: { disableToolbarButton: false },
                                          printOptions: { disableToolbarButton: false },
                                          showQuickFilter: true,
                                          quickFilterProps: { debounceMs: 250 },
                                        },
                                      }}
                                      pagination
                                      paginationMode="client"
                                      pageSize={15}
                                      rowsPerPageOptions={[15, 50, 100]}
                                      rows={viewInvoicesDialog.invoices}
                                      columns={[
                                        {
                                          field: "invoiceNumber",
                                          headerName: "Invoice Number",
                                          flex: 0.5,
                                        },
                                        {
                                          field: "invoiceDate",
                                          headerName: "Invoice Date",
                                          flex: 0.4,
                                          valueFormatter: (params) => moment(params?.value).format("DD-MM-YYYY"),
                                        },
                                        {
                                          field: "supplierId",
                                          headerName: "Supplier",
                                          flex: 0.5,
                                          valueGetter: (params) =>
                                            supplierData.find((supplier) => supplier.supplierId === params.row.supplierId)
                                              ?.supplierName || "Unknown",
                                        },
                                        {
                                          field: "currencyId",
                                          headerName: "Currency",
                                          flex: 0.3,
                                          valueGetter: (params) =>
                                            currencyData.find((cur) => cur.currencyId === params.row.currencyId)?.isoCode || "Unknown",
                                        },
                                        {
                                          field: "invoiceGrossAmount",
                                          headerName: "Net Amount",
                                          flex: 0.4,
                                          align: "right",
                                          renderCell: (params) => (
                                            <div style={{ textAlign: "right", paddingRight: "15px" }}>
                                              {params.value.toFixed(2).replace(".", ",")}
                                            </div>
                                          ),
                                        },
                                        {
                                          field: "invoiceVATAmountLocalCurrency",
                                          headerName: "VAT Amount",
                                          flex: 0.4,
                                          align: "right",
                                          renderCell: (params) => (
                                            <div style={{ textAlign: "right", paddingRight: "15px" }}>
                                              {params.value.toFixed(2).replace(".", ",")}
                                            </div>
                                          ),
                                        },
                                        {
                                          field: "fileId",
                                          headerName: "PDF Available",
                                          flex: 0.3,
                                          renderCell: (params) =>
                                            params.value > 0 ? (
                                              <CheckCircleOutlineIcon style={{ color: "green", margin: "0 auto" }} />
                                            ) : (
                                              <CheckCircleOutlineIcon style={{ color: "red", margin: "0 auto" }} />
                                            ),
                                        },
                                        {
                                          field: "actions",
                                          headerName: "Actions",
                                          flex: 0.3,
                                          sortable: false,
                                          renderCell: (params) => {
                                            return (
                                              <Box>
                                                <IconButton
                                                  onClick={async (e) => {
                                                    console.log("Invoice row data:", params.row);

                                                    try {
                                                      // Fetch complete invoice data using the same API as customerinvoices.js
                                                      const response = await axios.get(
                                                        `/api/v1/customerInvoicesByEntityIdWithProductCodes/${customerId}`
                                                      );
                                                      const completeInvoiceData = response.data.find(
                                                        (invoice) => invoice.customerInvoiceId === params.row.customerInvoiceId
                                                      );

                                                      if (completeInvoiceData) {
                                                        console.log("Complete invoice data:", completeInvoiceData);

                                                        setViewCustomerInvoiceDialog({
                                                          isOpen: true,
                                                          downloadFile: true,
                                                          title: "Manage Invoice",
                                                          params: completeInvoiceData,
                                                          custInfo: [{ customerId: customerId, customerNumber: customerNumber }],
                                                          productInfo: [
                                                            {
                                                              productCode: completeInvoiceData.claimProductCode,
                                                              productSubCode: completeInvoiceData.claimProductSubCode,
                                                            },
                                                          ],
                                                          subTitle:
                                                            "Are you sure you want to manage " + completeInvoiceData.contactName + "?",
                                                          onConfirm: () => {
                                                            onEditConfirmed(e, completeInvoiceData);
                                                          },
                                                          handleFile: () => {
                                                            handleFile(completeInvoiceData);
                                                          },
                                                        });
                                                      } else {
                                                        console.error("Invoice not found in complete data");
                                                        // Fallback to preview data
                                                        setViewCustomerInvoiceDialog({
                                                          isOpen: true,
                                                          downloadFile: true,
                                                          title: "Manage Invoice",
                                                          params: params.row,
                                                          custInfo: [{ customerId: customerId, customerNumber: customerNumber }],
                                                          productInfo: [
                                                            {
                                                              productCode: params.row.claimProductCode,
                                                              productSubCode: params.row.claimProductSubCode,
                                                            },
                                                          ],
                                                          subTitle: "Are you sure you want to manage " + params.row.contactName + "?",
                                                          onConfirm: () => {
                                                            onEditConfirmed(e, params.row);
                                                          },
                                                          handleFile: () => {
                                                            handleFile(params.row);
                                                          },
                                                        });
                                                      }
                                                    } catch (error) {
                                                      console.error("Error fetching complete invoice data:", error);
                                                      // Fallback to preview data
                                                      setViewCustomerInvoiceDialog({
                                                        isOpen: true,
                                                        downloadFile: true,
                                                        title: "Manage Invoice",
                                                        params: params.row,
                                                        custInfo: [{ customerId: customerId, customerNumber: customerNumber }],
                                                        productInfo: [
                                                          {
                                                            productCode: params.row.claimProductCode,
                                                            productSubCode: params.row.claimProductSubCode,
                                                          },
                                                        ],
                                                        subTitle: "Are you sure you want to manage " + params.row.contactName + "?",
                                                        onConfirm: () => {
                                                          onEditConfirmed(e, params.row);
                                                        },
                                                        handleFile: () => {
                                                          handleFile(params.row);
                                                        },
                                                      });
                                                    }
                                                  }}
                                                >
                                                  <VisibilityIcon />
                                                </IconButton>
                                              </Box>
                                            );
                                          },
                                        },
                                      ]}
                                      sx={{
                                        height: 500,
                                        width: "100%",
                                        borderRadius: 1,
                                        backgroundColor: "background.paper",
                                        boxShadow: 2,
                                        "& .MuiDataGrid-cell:hover": { color: "primary.main" },
                                        marginTop: 0,
                                      }}
                                    />
                                  </CardContent>
                                </Card>
                              </DialogContent>
                              <DialogActions>
                                <Button
                                  onClick={() => setViewInvoicesDialog({ isOpen: false, title: "", invoices: [] })}
                                  variant="contained"
                                  color="primary"
                                >
                                  Close
                                </Button>
                              </DialogActions>
                            </Dialog>
                            {/* ViewCustomerInvoiceDialog for individual invoice management */}
                            <ViewCustomerInvoiceDialog
                              key={viewCustomerInvoiceDialog.params?.customerInvoiceId || Date.now()}
                              viewCustomerInvoiceDialog={viewCustomerInvoiceDialog}
                              setViewCustomerInvoiceDialog={setViewCustomerInvoiceDialog}
                              countryArr={countryData}
                              supplierArr={supplierData}
                              currencyArr={currencyData}
                              productTypeArr={productData}
                              invoiceStateArr={invoiceStateData}
                              invoiceLabelArr={invoiceLabelData}
                              refreshGrid={() => {
                                // Refresh the invoice list when an invoice is updated
                                console.log("Refreshing invoice data...");

                                // Refresh the main preview data first
                                getPreviewData();

                                // If the invoice list dialog is open, refresh it directly with fresh API data
                                if (viewInvoicesDialog.isOpen) {
                                  // Find which country dialog is currently open by looking at the current invoices
                                  const currentInvoices = viewInvoicesDialog.invoices;
                                  if (currentInvoices.length > 0) {
                                    const countryId = currentInvoices[0].countryCode;
                                    console.log("Refreshing invoice list for country:", countryId);

                                    // Fetch fresh invoice data for this specific country
                                    const refreshInvoiceList = async () => {
                                      try {
                                        const response = await axios.get(
                                          `/api/v1/customerInvoicesByEntityIdWithProductCodes/${customerId}`
                                        );
                                        const countryInvoices = response.data.filter((invoice) => invoice.countryCode === countryId);

                                        setViewInvoicesDialog({
                                          ...viewInvoicesDialog,
                                          invoices: countryInvoices,
                                        });
                                        console.log(
                                          "Updated invoice list for country:",
                                          countryId,
                                          "with",
                                          countryInvoices.length,
                                          "invoices"
                                        );
                                      } catch (error) {
                                        console.error("Error refreshing invoice list:", error);
                                      }
                                    };

                                    refreshInvoiceList();
                                  }
                                }
                              }}
                            />
                            <ColorLegend />
                            <DataGrid
                              getRowId={(row) => row.id}
                              getRowClassName={getRowClassName}
                              disableColumnFilter
                              disableSelectionOnClick
                              disableColumnSelector
                              components={{ Toolbar: GridToolbar }}
                              density="compact"
                              componentsProps={{
                                toolbar: {
                                  csvOptions: { disableToolbarButton: false },
                                  printOptions: { disableToolbarButton: false },
                                  showQuickFilter: true,
                                  quickFilterProps: { debounceMs: 250 },
                                },
                              }}
                              rows={invoiceGridData}
                              columns={columns}
                              pageSize={15}
                              initialState={{
                                sorting: {
                                  sortModel: [{ field: "countryCode", sort: "asc" }],
                                },
                              }}
                              sx={{
                                height: 667,
                                width: "100%",
                                borderRadius: 1,
                                backgroundColor: "background.paper",
                                boxShadow: 2,
                                "& .MuiDataGrid-cell:hover": {
                                  color: "primary.main",
                                },
                                "& .MuiDataGrid-row:hover": {
                                  "& .pastelGreen .MuiDataGrid-cell": {
                                    color: "#ffffff !important",
                                  },
                                  "& .pastelRed .MuiDataGrid-cell": {
                                    color: "#ffffff !important",
                                  },
                                },
                                "& .pastelGreen:hover .MuiDataGrid-cell": {
                                  color: "#ffffff !important",
                                },
                                "& .pastelRed:hover .MuiDataGrid-cell": {
                                  color: "#ffffff !important",
                                },
                                "& .MuiDataGrid-row.pastelGreen:hover .MuiDataGrid-cell": {
                                  color: "#ffffff !important",
                                },
                                "& .MuiDataGrid-row.pastelRed:hover .MuiDataGrid-cell": {
                                  color: "#ffffff !important",
                                },
                                marginTop: 0,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              type="submit"
                              variant="contained"
                              color="secondary"
                              sx={
                                canWriteClaims
                                  ? { backgroundColor: "#f57c00", "&:hover": { backgroundColor: "#ef6c00" } }
                                  : undefined
                              }
                              mt={3}
                              onClick={handleAddClaim}
                              my={2}
                              mr={4}
                              disabled={!canWriteClaims}
                            >
                              Create Pre-Claims
                            </Button>
                            <Button
                              type="button"
                              variant="contained"
                              color="primary"
                              mt={3}
                              onClick={() => {
                                setOpen(false);
                                setInvoiceGridData([]);
                                setSelectedClaimPreview([]);
                              }}
                              my={2}
                            >
                              Cancel
                            </Button>
                          </Grid>
                        </CardContent>
                      </Card>
                    )}
                  </Formik>
                </DialogContent>
              </Dialog>
            </div>
          </Paper>
        </CardContent>
      </Card>
    );
  }
  return (
    <React.Fragment>
      <FormDialog />
    </React.Fragment>
  );
};
export default AddMultipleClaimForm;
